<template>
  <div class="inst-container">
    <div
      v-for="feature in selectedInstruments"
      :key="`${feature.reference_designator}-bar`"
      style="display: inline-block;"
    >
      <!--Inst icons in separate component -->
      <inst-icons
        :id="`inst-bar-${feature.reference_designator}`"
        :alt="`${feature.reference_designator}`"
        :instAT="feature.asset_type"
      ></inst-icons>
      <b-popover
        :target="`inst-bar-${feature.reference_designator}`"
        :ref="`popover-inst-bar-${feature.reference_designator}`"
        placement="bottom"
        triggers="hover"
        boundary="viewport"
        no-fade
      >
        <b-row class="overflow-auto mt-2">
          <b-col>
            <div class="flex-column align-items-start">
              <div class="d-flex w-100 justify-content-between">
                <h6 class="instrument-title">
                  <strong style="vertical-align: middle">{{
                    feature.formal_name
                  }}</strong>
                </h6>
              </div>
              <div>
                <span title="Reference Designator">
                  <i class="fas fa-hashtag"></i>
                </span>
                {{ feature.reference_designator }}
                <br />
                <span title="Status">
                  <!--Status indicator icon using nereus information-->
                  <i class="material-icons icon-color-dark">
                    {{getStatusIndicatorIcon(feature.reference_designator)}}
                  </i>
                </span>
                <!--Status indicator text using nereus information-->
                <span class="status-title">
                  {{getStatusIndicatorText(feature.reference_designator)}}
                </span>
                <br />
                <span title="Site Location">
                  <i class="fas fa-map-marked-alt"></i>
                </span>
                {{ getSiteName(feature.site_rd) }}
                <br />
                <span title="Infrastructure Location">
                  <i class="fas fa-map-marker-alt"></i>
                </span>
                {{ getInfraName(feature.infra_rd) }}
                <br />
                <span title="Description">
                  <i class="fas fa-align-right"></i>
                </span>
                {{ feature.description }}
                <br />
                <span title="Model">
                  <i class="fas fa-qrcode"></i>
                </span>
                {{ feature.model ? feature.model : "N/A" }}
                <br />
                <span title="Manufacturer">
                  <i class="fas fa-industry"></i>
                </span>
                {{ feature.manufacturer ? feature.manufacturer : "N/A" }}
                <br />
                <span title="Owner">
                  <i class="fas fa-user"></i>
                </span>
                {{ feature.owner ? feature.owner : "N/A" }}
              </div>
              <br>
              <b-button :id="`${feature.reference_designator}-dataset`"
                @click.prevent="removeTraceInstrument(feature)"
                size="sm"
                variant="primary"
                style="backgroundColor: #dc3545; border: #dc3545;"
              >Remove from dataset</b-button>
            </div>
          </b-col>
        </b-row>
      </b-popover>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapActions, mapGetters } from 'vuex';
import { iconNames } from '../../../../utils/constants';
import InstIconsVue from '../../../maps/instIcons';

export default {
  props: ['trace'],
  components: {
    'inst-icons': InstIconsVue,
  },
  data() {
    return {
      iconNames,
    };
  },
  computed: {
    ...mapState({
      instruments: (state) => state.instruments,
      sites: (state) => state.sites.features,
      infrastructures: (state) => state.infrastructures,
      statusIndicators: (state) => state.statusIndicators,
    }),
    ...mapGetters('visualization/traces', {
      getInstruments: 'findSelectedInstruments',
    }),
    ...mapGetters('visualization/charts', {
      getChart: 'find',
      charts: 'list',
      chartsLength: 'count',
    }),
    selectedInstruments() {
      if (this.trace) {
        const that = this;
        return this
          .getInstruments(that.trace.id)
          .map((o) => that.getInst(o.ref));
      }
      return [];
    },
    uuid() {
      return this.$route.params.id;
    },
    chart() {
      if (this.chartsLength === 0) {
        return null;
      }
      return this.getChart(this.uuid);
    },
  },
  methods: {
    ...mapActions('visualization/traces', [
      'removeInstrument',
      'resetTrace',
    ]),
    ...mapActions('visualization/charts', [
      'resetPlotLayout',
      'setHasDataFlag',
    ]),
    // Helper method to filter through the statusIndicator information
    getStatusHelper(refDes) {
      let statusValue = _.get(this.statusIndicators, refDes);
      if (_.isEmpty(statusValue)) {
        statusValue = 'unset';
      }
      return statusValue.toLowerCase();
    },
    // Takes the status information and displays "Status" text
    getStatusIndicatorText(refDes) {
      const statusHolderText = this.getStatusHelper(refDes);
      const newStr = statusHolderText.replace(/-|_/g, ' ').toLowerCase();
      let text;
      if (_.isEmpty(statusHolderText)) {
        text = 'No status available';
      } else {
        text = newStr[0].toUpperCase() + newStr.substring(1);
      }
      return text;
    },
    // Takes the status information and displays "Status" text
    getStatusIndicatorIcon(refDes) {
      const statusHolder = this.getStatusHelper(refDes).toLowerCase();
      return this.iconNames[statusHolder];
    },
    // Adds sitet name on instrument bar popup
    getSiteName(ref) {
      const siteName = _.filter(this.sites, [
        'properties.reference_designator', ref,
      ]);
      return siteName[0].properties.site_name;
    },
    // Adds infrastructure name on instrument bar popup
    getInfraName(ref) {
      const infraName = _.filter(this.infrastructures, [
        'reference_designator', ref,
      ]);
      return infraName[0].infraName;
    },
    getInst(refdes) {
      // Get instrument from instruments listing in state.
      // TODO: Change this to fetch from API for inst details!
      return _.find(this.instruments, ['reference_designator', refdes]);
    },
    removeTraceInstrument(inst) {
      // Checks if there is only 1 selected instrument.
      // If only 1 selected instrument and user wants to remove it,
      // a modal will pop up to confirm action and clear out the form and plot
      if (this.selectedInstruments.length === 1) {
        // Programmatically creates the confirmation modal
        this.$bvModal.msgBoxConfirm('Are you sure you want to remove the last instrument from the dataset? This will clear out the form and plot.', {
          title: 'Confirm removal of last instrument',
          size: 'lg',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'YES, REMOVE IT.',
          cancelVariant: 'primary',
          cancelTitle: 'NO, DO NOT REMOVE IT.',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        })
          .then((value) => {
            // If value is true (user selects ok), calls the function to handle that sel
            if (value) {
              this.handleOk(inst.reference_designator);
            }
          })
          .catch(() => {
            // An error occurred
          });
      } else {
        // Removes the instrument dataset from the instrument row if clicked
        this.handleOk(inst.reference_designator);
      }
    },
    // Puts the removeTraceSelectedInstrument into its own method
    removeSelectedInstrument(uuid, ref) {
      this.removeTraceSelectedInstruments({
        uuid,
        ref,
      });
    },
    // If user selects that they want to remove the last instrument, the program will:
    // Reset and clear out the form and chart
    // Remove the last selected instrument so the length is 0
    handleOk(ref) {
      this.resetTrace({ id: this.trace.id });
      this.resetPlotLayout({
        uuid: this.uuid,
      });
      this.setHasDataFlag({ uuid: this.uuid, hasData: false });

      /** Clear out url queries that has to do with params */
      const query = {
        ...this.$route.query,
      };
      const finalQuery = _.omitBy(query, (value, key) => {
        if (['x', 'y'].includes(key) || key.includes('color') || key.includes('dt')) {
          return value;
        }
        return null;
      });

      this.removeInstrument({ id: this.trace.id, ref });

      /** Reset url path */
      this.$router.push({ query: finalQuery });
    },
  },
};
</script>

<style>
.inst-container {
  overflow: auto;
  white-space: nowrap;
}
.icon-color-dark {
  color: black !important;
  font-size: 1rem !important;
  margin-left: -0.15rem !important;
  margin-top: 0.15rem !important;
  position: fixed !important;
}
.status-title {
  margin-left: 1rem;
}
</style>
